import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Configuration interface for date formatting settings.
 *
 * @interface DateFormatConfig
 * @example
 * ```typescript
 * const config: DateFormatConfig = {
 *   format: 'MM/dd/yyyy',
 *   displayFormat: 'MM/dd/yyyy',
 *   parseFormat: 'MM/dd/yyyy'
 * };
 * ```
 */
export interface DateFormatConfig {
  /**
   * General date format string used as default.
   * @example 'MM/dd/yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'
   */
  format: string;

  /**
   * Format string used for displaying dates in UI components.
   * @example 'MM/dd/yyyy' displays as '08/10/2025'
   */
  displayFormat: string;

  /**
   * Format string used for parsing user input into Date objects.
   * @example 'MM/dd/yyyy' parses '08/10/2025' to Date object
   */
  parseFormat: string;
}

/**
 * Service for managing application-wide date formatting configuration.
 *
 * This service provides centralized date formatting settings that can be used
 * across the entire application. It supports reactive updates, allowing
 * components to respond to format changes automatically.
 *
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private dateConfigService: DateConfigService) {}
 *
 * // Get current config
 * const config = this.dateConfigService.getCurrentConfig();
 *
 * // Format a date
 * const formatted = this.dateConfigService.formatDate(new Date());
 *
 * // Update format globally
 * this.dateConfigService.updateConfig({ displayFormat: 'dd/MM/yyyy' });
 *
 * // Subscribe to config changes
 * this.dateConfigService.config$.subscribe(config => {
 *   console.log('New format:', config.displayFormat);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
/**
 * Service for managing and providing date formatting configuration across the application.
 *
 * Allows reactive updates and retrieval of date format settings.
 */
export class DateConfigService {
  /**
   * Default date configuration used when no custom config is provided.
   * Uses US format (MM/dd/yyyy) with leading zeros.
   *
   * @private
   * @readonly
   */
  private readonly defaultConfig: DateFormatConfig = {
    format: 'MM/dd/yyyy',
    displayFormat: 'MM/dd/yyyy',
    parseFormat: 'MM/dd/yyyy'
  };

  /**
   * BehaviorSubject that holds the current date configuration.
   * Allows reactive updates when configuration changes.
   *
   * @private
   */
  private configSubject = new BehaviorSubject<DateFormatConfig>(this.defaultConfig);

  /**
   * Observable stream of date configuration changes.
   * Components can subscribe to this to react to format updates.
   *
   * @public
   * @readonly
   *
   * @example
   * ```typescript
   * this.dateConfigService.config$.subscribe(config => {
   *   console.log('Format changed to:', config.displayFormat);
   * });
   * ```
   */
  public readonly config$: Observable<DateFormatConfig> = this.configSubject.asObservable();

  /**
   * Gets the current date format configuration.
   *
   * @returns {DateFormatConfig} The current configuration object
   *
   * @example
   * ```typescript
   * const config = this.dateConfigService.getCurrentConfig();
   * console.log('Current display format:', config.displayFormat);
   * // Output: "Current display format: MM/dd/yyyy"
   * ```
   */
  getCurrentConfig(): DateFormatConfig {
    return this.configSubject.value;
  }

  /**
   * Updates the date format configuration with new settings.
   * Only provided properties will be updated; others remain unchanged.
   *
   * @param {Partial<DateFormatConfig>} config - Partial configuration object with properties to update
   *
   * @example
   * ```typescript
   * // Update only the display format
   * this.dateConfigService.updateConfig({
   *   displayFormat: 'dd/MM/yyyy'
   * });
   *
   * // Update multiple properties
   * this.dateConfigService.updateConfig({
   *   displayFormat: 'yyyy-MM-dd',
   *   parseFormat: 'yyyy-MM-dd'
   * });
   * ```
   */
  updateConfig(config: Partial<DateFormatConfig>): void {
    const updatedConfig = { ...this.getCurrentConfig(), ...config };
    this.configSubject.next(updatedConfig);
  }

  /**
   * Formats a date according to the current display format configuration.
   *
   * @param {Date | string | null} date - The date to format. Can be Date object, date string, or null
   * @returns {string} Formatted date string, or empty string if date is invalid/null
   *
   * @example
   * ```typescript
   * const date = new Date(2025, 7, 10); // August 10, 2025
   *
   * // With default MM/dd/yyyy format
   * const formatted = this.dateConfigService.formatDate(date);
   * console.log(formatted); // "08/10/2025"
   *
   * // With string input
   * const formatted2 = this.dateConfigService.formatDate('2025-08-10');
   * console.log(formatted2); // "08/10/2025"
   *
   * // With null input
   * const formatted3 = this.dateConfigService.formatDate(null);
   * console.log(formatted3); // ""
   *
   * // After changing format
   * this.dateConfigService.updateConfig({ displayFormat: 'dd/MM/yyyy' });
   * const formatted4 = this.dateConfigService.formatDate(date);
   * console.log(formatted4); // "10/08/2025"
   * ```
   */
  formatDate(date: Date | string | null): string {
    if (!date) return '';

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    const config = this.getCurrentConfig();
    return this.applyFormat(dateObj, config.displayFormat);
  }

  /**
   * Applies a specific format pattern to a Date object.
   *
   * @private
   * @param {Date} date - The Date object to format
   * @param {string} format - The format pattern to apply
   * @returns {string} The formatted date string
   *
   * @remarks
   * Supports the following format tokens:
   * - `MM` - Month with leading zero (01-12)
   * - `dd` - Day with leading zero (01-31)
   * - `yyyy` - Full year (e.g., 2025)
   *
   * @example
   * ```typescript
   * const date = new Date(2025, 7, 5); // August 5, 2025
   *
   * this.applyFormat(date, 'MM/dd/yyyy'); // "08/05/2025"
   * this.applyFormat(date, 'dd/MM/yyyy'); // "05/08/2025"
   * this.applyFormat(date, 'yyyy-MM-dd'); // "2025-08-05"
   * ```
   */
  private applyFormat(date: Date, format: string): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return format
      .replace('MM', month)
      .replace('dd', day)
      .replace('yyyy', year);
  }

  /**
   * Resets the date configuration to default values.
   *
   * @example
   * ```typescript
   * // After making changes
   * this.dateConfigService.updateConfig({ displayFormat: 'dd/MM/yyyy' });
   *
   * // Reset to defaults
   * this.dateConfigService.resetToDefaults();
   *
   * const config = this.dateConfigService.getCurrentConfig();
   * console.log(config.displayFormat); // "MM/dd/yyyy"
   * ```
   */
  resetToDefaults(): void {
    this.configSubject.next({ ...this.defaultConfig });
  }

  /**
   * Checks if the provided format string is valid.
   *
   * @param {string} format - The format string to validate
   * @returns {boolean} True if format is valid, false otherwise
   *
   * @example
   * ```typescript
   * this.dateConfigService.isValidFormat('MM/dd/yyyy'); // true
   * this.dateConfigService.isValidFormat('dd/MM/yyyy'); // true
   * this.dateConfigService.isValidFormat('invalid');    // false
   * ```
   */
  isValidFormat(format: string): boolean {
    // Check if format contains required tokens
    const hasMonth = format.includes('MM');
    const hasDay = format.includes('dd');
    const hasYear = format.includes('yyyy');

    return hasMonth && hasDay && hasYear;
  }

  /**
   * Gets a list of commonly used date formats.
   *
   * @returns {Array<{label: string, value: string}>} Array of format options
   *
   * @example
   * ```typescript
   * const formats = this.dateConfigService.getCommonFormats();
   * formats.forEach(format => {
   *   console.log(`${format.label}: ${format.value}`);
   * });
   * // Output:
   * // "US Format: MM/dd/yyyy"
   * // "European Format: dd/MM/yyyy"
   * // "ISO Format: yyyy-MM-dd"
   * ```
   */
  getCommonFormats(): Array<{label: string, value: string}> {
    return [
      { label: 'US Format', value: 'MM/dd/yyyy' },
      { label: 'European Format', value: 'dd/MM/yyyy' },
      { label: 'ISO Format', value: 'yyyy-MM-dd' },
      { label: 'Dotted Format', value: 'MM.dd.yyyy' },
      { label: 'Dashed Format', value: 'MM-dd-yyyy' }
    ];
  }
}
